import EventEmitter from 'eventemitter3';
import PQueue from 'p-queue';
import { ulid } from 'ulid';

import { AbortMap } from '@iconik-io/core/src/abort_controller/abort_map';
import type { EventsMap } from '@iconik-io/core/src/events/types';
import type { RequiredBy } from '@iconik-io/core/src/types';
import type { IconikApiClientInterface } from '@iconik-io/iconik-api/src/client';

import { CachedStorageOptionsProvider } from './cached_storage_options_provider';
import type { UploaderHttpClientInterface } from './client/client';
import type { CollectionsServiceInterface } from './collections_service';
import {
  NoStorageSelectedError,
  UnsupportedStorageMethod,
  UnsupportedUploadKindError,
} from './errors';
import { AzureUploader } from './services/azure/uploader';
import { B2Uploader } from './services/b2/uploader';
import type { EntitiesLifecycle } from './services/entities_lifecycle/entities_lifecycle';
import { fileEntitiesLifecycle } from './services/entities_lifecycle/file_entities_lifecycle';
import { proxyEntitiesLifecycle } from './services/entities_lifecycle/proxy_entities_lifecycle';
import {
  fileMultipartEndpoints,
  type MultipartEndpoints,
  proxyMultipartEndpoints,
} from './services/entity_endpoints';
import type {
  MultipartUploaderEvents,
  MultipartUploaderEventsMap,
} from './services/events';
import { GcsUploader } from './services/gcs/uploader';
import { GcsWholeFileUploader } from './services/gcs/whole_file_uploader';
import { S3Uploader } from './services/s3/uploader';
import {
  type CalculateChecksum,
  type FileInfo,
  type GetSourceStream,
  UPLOADER_STATUS_COMPLETED,
  UPLOADER_STATUS_COMPLETING,
  UPLOADER_STATUS_FAILED,
  UPLOADER_STATUS_PAUSED,
  UPLOADER_STATUS_WAITING,
  type UploaderState,
  type UploaderStateStorageInterface,
  type UploaderStateUpdater,
  type UploaderTarget,
  type UploadKind,
  type UploadKindType,
  type UploadOptions,
} from './state';
import { hasCreatedEntities } from './state/upload_state';
import type { StorageMethod, StorageOptions } from './types';
import { needsCancelling } from './utils/needs_cancelling';

const DEFAULT_UPLOAD_CONCURRENCY = 3;

export type IconikUploadSchedulerEvents = MultipartUploaderEvents<
  unknown,
  unknown
>;

type Uploaders =
  | typeof GcsUploader
  | typeof GcsWholeFileUploader
  | typeof B2Uploader
  | typeof S3Uploader
  | typeof AzureUploader;

type GetStorageOptions = (id: string) => Promise<StorageOptions>;

type SchedulerOptions = {
  numConcurrentUploads?: number;
  getStorageOptions?: GetStorageOptions;
  calculateChecksum?: CalculateChecksum;
};

/**
 * What the upload is for. Both default to today's behavior: creating a new
 * asset and uploading an ORIGINAL file into it.
 */
type QueuedUpload = {
  target?: UploaderTarget;
  uploadKind?: UploadKind;
};

/** What can still change about an upload that is not being uploaded. */
type UploadUpdate = {
  options?: Partial<UploadOptions>;
  targetPath?: string;
};

/** A run from run() until it settles: waiting in the queue, then uploading. */
type ActiveRun = {
  settled: Promise<void>;
  /** Set once the run's turn in the queue comes. */
  uploader?: SchedulableUploader;
  /** Paused or cancelled, but not settled yet. */
  stopping: boolean;
};

const proxiedUploaderEvents = [
  'runUpload',
  'prepareEntities',
  'paused',
  'cancelled',
  'completed',
  'chunkCompleted',
  'error',
  'progress',
  'metaUpdated',
  'chunkUpdated',
  'chunkMetaUpdated',
  'assetCreated',
  'versionCreated',
  'formatCreated',
  'fileSetCreated',
  'fileCreated',
  'proxyCreated',
  'log',
] as const;

/**
 * What the scheduler needs from an uploader.
 *
 * Deliberately narrower than MultipartUploader: that class holds a private
 * chunk-completed handler taking ChunkMeta, which makes it invariant in its
 * meta type parameters, so no concrete provider is assignable to
 * MultipartUploader<unknown, unknown>. The scheduler never touches provider
 * meta, so it does not need that surface.
 */
interface SchedulableUploader
  extends EventEmitter<MultipartUploaderEventsMap<unknown, unknown>> {
  init(): void;
  run(): Promise<void>;
  pause(): Promise<void>;
  cancel(): Promise<void>;
}

/**
 * Which uploader moves the bytes. Both axes matter: proxies have no multipart
 * API on GCS and no endpoints at all on B2.
 *
 * Returns undefined for a storage method that cannot be uploaded to at all, and
 * throws for one that only this upload kind cannot use.
 */
function resolveUploader(
  method: StorageMethod,
  kind: UploadKindType,
): Uploaders | undefined {
  switch (method) {
    case 'S3':
      return S3Uploader;
    case 'AZURE':
      return AzureUploader;
    case 'GCS':
      return kind === 'proxy' ? GcsWholeFileUploader : GcsUploader;
    case 'B2':
      if (kind === 'proxy') {
        throw new UnsupportedUploadKindError(method, kind);
      }
      return B2Uploader;
    default:
      return undefined;
  }
}

const endpointsByKind: Record<UploadKindType, MultipartEndpoints> = {
  file: fileMultipartEndpoints,
  proxy: proxyMultipartEndpoints,
};

const lifecycleByKind: Record<UploadKindType, EntitiesLifecycle> = {
  file: fileEntitiesLifecycle,
  proxy: proxyEntitiesLifecycle,
};

export class IconikUploadScheduler extends EventEmitter<
  EventsMap<IconikUploadSchedulerEvents>
> {
  #uploadingQueue: PQueue;

  // Allows aborting jobs before they have been started
  #jobAbortControllers = new AbortMap();

  #activeRuns = new Map<string, ActiveRun>();

  #getStorageOptions: GetStorageOptions;

  #calculateChecksum: CalculateChecksum | undefined;

  constructor(
    protected apiClient: IconikApiClientInterface,
    protected httpClient: UploaderHttpClientInterface,
    private collectionsService: CollectionsServiceInterface,
    private getSourceStream: GetSourceStream,
    private storage: UploaderStateStorageInterface,
    options?: SchedulerOptions,
  ) {
    super();

    this.#getStorageOptions =
      options?.getStorageOptions ?? this.#getDefaultStorageOptionsProvider();
    this.#calculateChecksum = options?.calculateChecksum;

    this.#uploadingQueue = new PQueue({
      concurrency: options?.numConcurrentUploads ?? DEFAULT_UPLOAD_CONCURRENCY,
    });
  }

  #getDefaultStorageOptionsProvider() {
    const defaultStorageOptionsProvider = new CachedStorageOptionsProvider(
      this.apiClient,
    );
    return defaultStorageOptionsProvider.getStorageOptions.bind(
      defaultStorageOptionsProvider,
    );
  }

  async queue(
    fileInfo: FileInfo,
    options: UploadOptions,
    upload: QueuedUpload = {},
  ): Promise<UploaderState> {
    const id = ulid();
    const state: UploaderState = {
      id,
      fileInfo,
      options,
      target: upload.target ?? { type: 'newAsset' },
      uploadKind: upload.uploadKind ?? { kind: 'file' },
      status: UPLOADER_STATUS_PAUSED,
    };
    await this.storage.set(state.id, state);

    return state;
  }

  async #changeState(
    stateId: string,
    updater: UploaderStateUpdater,
  ): Promise<UploaderState | undefined> {
    const updatedState = await this.storage.update(stateId, updater);
    if (updatedState) {
      this.emit('stateUpdated', { state: updatedState });
    }
    return updatedState;
  }

  #updateState(
    update: RequiredBy<Partial<UploaderState>, 'id'>,
  ): Promise<UploaderState | undefined> {
    return this.#changeState(update.id, (state) => ({ ...state, ...update }));
  }

  async #makeUploader(state: UploaderState): Promise<SchedulableUploader> {
    try {
      const storageId = state.options.storageId;
      if (!storageId) {
        throw new NoStorageSelectedError();
      }
      const storageOptions = await this.#getStorageOptions(storageId);

      const uploadKind = state.uploadKind?.kind ?? 'file';
      const UploaderClass = resolveUploader(storageOptions.method, uploadKind);
      if (!UploaderClass) {
        throw new UnsupportedStorageMethod(storageId, storageOptions.method);
      }

      const uploader: SchedulableUploader = new UploaderClass({
        apiClient: this.apiClient,
        httpClient: this.httpClient,
        collectionsManager: this.collectionsService,
        getSourceStream: this.getSourceStream,
        calculateChecksum: this.#calculateChecksum,
        // Persisted state carries an unknown meta, while each provider declares
        // its own. A bottom type is the only value assignable to every member
        // of the uploader-constructor union.
        // biome-ignore lint/plugin: narrowing this soundly needs runtime validation of persisted provider meta.
        state: state as UploaderState<never, never>,
        storageOptions,
        endpoints: endpointsByKind[uploadKind],
        entitiesLifecycle: lifecycleByKind[uploadKind],
      });

      uploader.on('stateUpdated', (event) => {
        this.#changeState(state.id, (current) => ({
          ...current,
          ...event.state,
        }));
      });

      proxiedUploaderEvents.forEach((eventName) => {
        uploader.on(eventName, (...args) => {
          this.emit(eventName, { ...args[0] });
        });
      });

      uploader.init();

      return uploader;
    } catch (error) {
      const updatedState = await this.#updateState({
        id: state.id,
        status: UPLOADER_STATUS_FAILED,
      });
      if (error instanceof Error && updatedState) {
        this.emit('error', { state: updatedState, error });
      }
      throw error;
    }
  }

  #getUploadTask(
    activeRun: ActiveRun,
    stateId: string,
    abortSignal: AbortSignal,
  ) {
    return async () => {
      const state = await this.storage.get(stateId);
      if (!state) {
        return Promise.resolve();
      }

      const uploader = await this.#makeUploader(state);

      activeRun.uploader = uploader;

      // Check the abort signal after the uploader has been set on the run.
      // Otherwise there is a potential race condition where the pause method signals
      // the abort after we have checked the signal but before we have set an Uploader in items.
      if (abortSignal.aborted) {
        return Promise.resolve();
      }

      return uploader.run();
    };
  }

  async runAll(): Promise<void> {
    const uploadPromises = [];
    for (const state of await this.storage.getAll()) {
      uploadPromises.push(this.run(state.id));
    }
    await Promise.all(uploadPromises);
  }

  /**
   * Starts or resumes an upload and settles once it stops. Running an upload
   * that is already waiting or uploading returns that run. One that was paused
   * or cancelled while running starts again once its run has settled.
   */
  run(stateId: string): Promise<void> {
    const activeRun = this.#activeRuns.get(stateId);
    if (activeRun) {
      if (!activeRun.stopping) {
        return activeRun.settled;
      }
      return activeRun.settled
        .catch(() => undefined)
        .then(() => this.run(stateId));
    }
    const newRun: ActiveRun = { settled: Promise.resolve(), stopping: false };
    this.#activeRuns.set(stateId, newRun);
    newRun.settled = this.#run(newRun, stateId).finally(() => {
      this.#activeRuns.delete(stateId);
    });
    return newRun.settled;
  }

  async #run(activeRun: ActiveRun, stateId: string): Promise<void> {
    const state = await this.storage.get(stateId);
    if (!state || state.status === UPLOADER_STATUS_COMPLETED) {
      return;
    }

    const waitingState = await this.#updateState({
      id: state.id,
      status: UPLOADER_STATUS_WAITING,
    });
    // Removed while this run was starting.
    if (!waitingState) {
      return;
    }

    await this.#uploadingQueue.add(
      this.#getUploadTask(
        activeRun,
        state.id,
        this.#jobAbortControllers.newSignal(stateId),
      ),
    );
  }

  async pause(stateId: string): Promise<void> {
    const state = await this.storage.get(stateId);
    if (
      !state ||
      state.status === UPLOADER_STATUS_PAUSED ||
      state.status === UPLOADER_STATUS_COMPLETED
    ) {
      return;
    }
    this.#markStopping(stateId);
    this.#jobAbortControllers.abort(stateId);

    // It can happen that we don't have a running uploader at this point, e.g. the app crashed
    // or was shut down without the uploads being paused first. In this case we create a temporary
    // uploader to be able to pause and reset the state of chunks
    const uploader =
      this.#activeRuns.get(stateId)?.uploader ??
      (await this.#makeUploader(state));
    await uploader.pause();
  }

  /**
   * Stops the upload and undoes what it created in Iconik, leaving it as
   * queue() did. An upload that created nothing yet is left alone.
   */
  async cancel(stateId: string): Promise<void> {
    const state = await this.storage.get(stateId);
    if (!state || state.status === UPLOADER_STATUS_COMPLETED) {
      return;
    }
    this.#markStopping(stateId);
    this.#jobAbortControllers.abort(stateId);

    // If we have a running upload we make sure to abort that. Otherwise we make a
    // temporary uploader object to be able to cancel. Canceling properly cleans up
    // chunks and created entities in iconik.
    const runningUploader = this.#activeRuns.get(stateId)?.uploader;
    if (!runningUploader && !needsCancelling(state)) {
      // One still waiting for its turn would otherwise stay WAITING, since its
      // task exits on the aborted signal without touching the status.
      if (state.status === UPLOADER_STATUS_WAITING) {
        await this.#updateState({
          id: stateId,
          status: UPLOADER_STATUS_PAUSED,
        });
      }
      return;
    }
    const uploader = runningUploader ?? (await this.#makeUploader(state));
    await uploader.cancel();
  }

  #markStopping(stateId: string) {
    const activeRun = this.#activeRuns.get(stateId);
    if (activeRun) {
      activeRun.stopping = true;
    }
  }

  /**
   * Takes an upload off the list: deletes its state, stops it, and undoes what
   * it created in Iconik. One that is completing is left to finish instead,
   * since its file is already uploaded. The state goes first so that
   * the list does not wait for the cancel. A cancel that fails, on a storage the
   * upload never got to use for instance, is only logged, since the upload goes
   * either way.
   */
  async remove(stateId: string): Promise<void> {
    this.#markStopping(stateId);
    this.#jobAbortControllers.abort(stateId);

    const state = await this.storage.get(stateId);
    await this.storage.delete(stateId);

    const uploader = this.#activeRuns.get(stateId)?.uploader;
    try {
      if (state?.status === UPLOADER_STATUS_COMPLETING) {
        return;
      }
      if (uploader) {
        await uploader.cancel();
      } else if (state && needsCancelling(state)) {
        await (await this.#makeUploader(state)).cancel();
      }
    } catch (error) {
      if (state) {
        this.emit('log', {
          level: 'warn',
          message: `Failed cancelling the removed upload ${stateId}`,
          error: error instanceof Error ? error : undefined,
          state,
        });
      }
    }
  }

  /** Renames the file to upload, see update. */
  rename(
    stateId: string,
    customFileName: string,
  ): Promise<UploaderState | undefined> {
    return this.update(stateId, { options: { customFileName } });
  }

  /**
   * Changes the options or target path of an upload that is not being
   * uploaded. One waiting in the queue picks the change up when it starts. An
   * upload that failed before creating anything, on a name that already exists
   * for instance, goes back to how queue() left it, so that it can start over
   * with the change. Changing an upload that is being uploaded is refused,
   * since its uploader would overwrite the change. Once it was paused or
   * cancelled, its uploader has written all it will.
   */
  async update(
    stateId: string,
    { options, targetPath }: UploadUpdate,
  ): Promise<UploaderState | undefined> {
    const activeRun = this.#activeRuns.get(stateId);
    if (activeRun?.uploader && !activeRun.stopping) {
      return undefined;
    }
    return this.#changeState(stateId, (state) => {
      const isReset =
        state.status === UPLOADER_STATUS_FAILED && !hasCreatedEntities(state);
      return {
        ...state,
        options: { ...state.options, ...options },
        ...(targetPath === undefined
          ? {}
          : { fileInfo: { ...state.fileInfo, targetPath } }),
        ...(isReset
          ? { status: UPLOADER_STATUS_PAUSED, chunks: undefined }
          : {}),
      };
    });
  }
}
